/**
 * @file StaleDraftDaysField.tsx
 * @description Поле «Черновик считается зависшим через N дней» в политиках
 * склада (phase19.24).
 *
 * Отдельным файлом, а не строкой в `WarehousePoliciesPanel`: у поля своя
 * валидация и подпись-объяснение под ним, а панель политик и так близка к
 * лимиту размера.
 *
 * Шаг стрелок, клавиатуру и запрет дробей берём из реестра `qty-input`
 * (единица «дн» — целая): своя проверка в форме печатала бы ошибку чужими
 * словами и разъезжалась бы с остальными числовыми полями склада.
 *
 * phase19.31 (зона «Склад»): поле `Input` кабинета, высота из темы, цифры
 * обычным шрифтом (моноширинный — только для кодов, SC43).
 */

import { useId } from "react";

import { Input } from "../../ui/input";
import { qtyInputProps } from "../../../lib/qty-input";

/**
 * Контракт поля: единица реестра `qty-input` («дн» — целое, шаг 1), границы
 * сервера (`staleDraftDays` 1..90) и его же дефолт. Панель политик берёт их
 * отсюда — и для проверки перед PATCH, и для значения у старых складов, где
 * настройки ещё без поля.
 */
export const STALE_DRAFT_DAYS = {
  unit: "дн",
  min: 1,
  max: 90,
  default: 3,
} as const;

interface Props {
  /** Значение строкой: поле можно очистить и дописать, не прыгая в 0. */
  value: string;
  /** Ошибка ввода или ответ 400 сервера; null — показываем только подсказку. */
  error: string | null;
  onChange: (next: string) => void;
}

export function StaleDraftDaysField({ value, error, onChange }: Props) {
  const fieldId = useId();
  const hintId = `${fieldId}-hint`;
  const input = qtyInputProps(STALE_DRAFT_DAYS.unit);

  return (
    <div className="flex flex-col gap-1 py-2">
      {/* min-w-0 даёт подписи переноситься на 360px; высота строки — из темы */}
      <div className="flex min-h-[var(--control-h,3rem)] items-center justify-between gap-4">
        <label htmlFor={fieldId} className="type-body min-w-0 font-medium text-on-surface">
          Черновик считается зависшим через N дней
        </label>
        <Input
          id={fieldId}
          type="number"
          value={value}
          min={STALE_DRAFT_DAYS.min}
          max={STALE_DRAFT_DAYS.max}
          step={input.step}
          inputMode={input.inputMode}
          aria-invalid={error ? true : undefined}
          aria-describedby={hintId}
          onChange={(e) => onChange(e.target.value)}
          className="w-24 shrink-0 text-right tabular-nums"
        />
      </div>
      <p
        id={hintId}
        className={error ? "type-caption text-[var(--destructive-text)]" : "type-caption text-on-surface-variant"}
      >
        {error ?? "Такие черновики попадают в «Требует внимания» на дашборде"}
      </p>
    </div>
  );
}
